// ProfileSettings.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ProfilePicture from '../components/ProfilePicture';
import api from '../services/api';

const ProfileSettings = () => {
  const { user, loadUser } = useAuth();
  const [uploading, setUploading] = useState(false);

  // ================================
  // HANDLE PROFILE PICTURE UPLOAD
  // ================================
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('profile_picture', file);

    try {
      setUploading(true);

      await api.post('auth/upload-profile-picture/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await loadUser();
      alert('Profile picture updated successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert(error.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  // ================================
  // HANDLE DELETE PROFILE PICTURE
  // ================================
  const handleDeletePicture = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) return;

    try {
      await api.delete('auth/delete-profile-picture/');
      await loadUser();
      alert('Profile picture deleted successfully!');
    } catch (error) {
      console.error('Delete failed:', error);
      alert(error.response?.data?.error || 'Failed to delete profile picture');
    }
  };

  // ================================
  // RENDER UI
  // ================================
  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-2xl mx-auto px-4">

        {/* White Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-neutral-900 mb-6">Profile Settings</h1>

          {/* Profile Picture + User Info */}
          <div className="flex items-center space-x-6 mb-8">
            <ProfilePicture user={user} size={80} />
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">{user?.username}</h2>
              <p className="text-neutral-600">{user?.email}</p>
              <p className="text-sm text-neutral-500 mt-1">
                {user?.profile?.profile_picture_url ? 'Profile picture set' : 'No profile picture'}
              </p>
            </div>
          </div>

          {/* Upload Profile Picture */}
          <div className="border-t pt-6">
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              Update Profile Picture
            </label>

            <div className="space-y-3">
              <input
                id="profile-picture-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="block w-full text-sm text-neutral-500 
                  file:mr-4 file:py-2 file:px-4 file:rounded-lg 
                  file:border-0 file:text-sm file:font-semibold 
                  file:bg-primary-50 file:text-primary-700 
                  hover:file:bg-primary-100"
              />

              {uploading && <p className="text-sm text-blue-600">Uploading...</p>}

              {user?.profile?.profile_picture_url && (
                <button
                  onClick={handleDeletePicture}
                  className="text-red-600 text-sm hover:text-red-800 font-medium"
                >
                  Remove profile picture
                </button>
              )}
            </div>
          </div>

          {/* Profile Information */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-neutral-900 mb-3">Profile Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700">Username</label>
                <p className="mt-1 text-sm text-neutral-900">{user?.username}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700">Email</label>
                <p className="mt-1 text-sm text-neutral-900">{user?.email}</p>
                <p className="text-xs text-neutral-500 mt-1">
                  {user?.profile?.email_verified ? '✓ Verified' : 'Not Verified'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700">Phone</label>
                <p className="mt-1 text-sm text-neutral-900">
                  {user?.profile?.phone_number || 'Not set'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700">KYC Status</label>
                <p className="mt-1 text-sm text-neutral-900">
                  {user?.profile?.kyc_verified ? 'Verified' : 'Not Verified'}
                </p>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-neutral-900 mb-4">Change Password</h3>

            <form
              onSubmit={async (e) => {
                e.preventDefault();

                const old_password = e.target.old_password.value;
                const new_password = e.target.new_password.value;
                const confirm_new_password = e.target.confirm_new_password.value;

                try {
                  await api.put('auth/change-password/', {
                    old_password,
                    new_password,
                    confirm_new_password,
                  });

                  alert('Password updated successfully!');
                  e.target.reset();
                } catch (err) {
                  alert(err.message || 'Failed to update password');
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-neutral-700">Old Password</label>
                <input
                  type="password"
                  name="old_password"
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700">New Password</label>
                <input
                  type="password"
                  name="new_password"
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirm_new_password"
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg"
                />
              </div>

              <button
                type="submit"
                className="bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700"
              >
                Update Password
              </button>
            </form>
          </div>
        </div> {/* END WHITE CARD */}
      </div>
    </div>
  );
};

export default ProfileSettings;
